import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PAYMENT_METHOD_LABELS, voidSaleSchema, type SaleDTO } from '@bifeai/shared';
import { Badge, Button, Callout, Dialog, Field, Skeleton, Textarea, useToast } from '@bifeai/ui';
import { Ban, Printer, RotateCw } from 'lucide-react';
import { useState } from 'react';
import { api, errorMessage } from '../lib/api';
import { formatDateTime, formatMoney } from '../lib/format';
import { printSale } from '../lib/ticket-print';
import { FiscalDocsPanel } from './FiscalLine';
import { formatQty, priceUnit } from '../lib/units';

export function SaleDetailDialog({ saleId, onClose }: { saleId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const sale = useQuery({
    queryKey: ['sales', 'detail', saleId],
    queryFn: () => api.get<SaleDTO>(`/sales/${saleId}`),
  });
  const [voiding, setVoiding] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function doVoid() {
    const parsed = voidSaleSchema.safeParse({ reason });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'Contá el motivo.');
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<SaleDTO>(`/sales/${saleId}/void`, parsed.data);
      const pending = r.mpRefunds.filter((x) => x.status === 'PENDING');
      if (pending.length) {
        toast.error(
          'Venta anulada, pero la plata no se devolvió',
          pending[0]!.error ?? 'Mercado Pago no hizo la devolución. Revisala en el detalle de la venta.',
        );
      } else {
        toast.success(
          'Venta anulada',
          r.mpRefunds.length ? 'El stock volvió a su lugar y Mercado Pago le devolvió la plata al cliente.' : 'El stock volvió a su lugar.',
        );
      }
      setVoiding(false);
      await Promise.all(['sales', 'pos', 'cash', 'stock', 'dashboard'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function retryRefund() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<SaleDTO>(`/sales/${saleId}/refund`);
      qc.setQueryData(['sales', 'detail', saleId], r);
      const pending = r.mpRefunds.find((x) => x.status === 'PENDING');
      if (pending) toast.error('Todavía no se pudo devolver', pending.error ?? undefined);
      else toast.success('Devolución hecha', 'Mercado Pago le devolvió la plata al cliente.');
      await qc.invalidateQueries({ queryKey: ['sales'] });
    } catch (err) {
      toast.error('No se pudo reintentar', errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const s = sale.data;
  const pendingRefunds = s?.mpRefunds.filter((r) => r.status === 'PENDING') ?? [];
  const doneRefunds = s?.mpRefunds.filter((r) => r.status === 'REFUNDED') ?? [];
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()} title={s ? `Ticket ${s.ticket}` : 'Venta'} wide>
      {!s ? (
        <div style={{ display: 'grid', gap: 10 }}>
          <Skeleton />
          <Skeleton />
        </div>
      ) : (
        <div className="bf-form">
          {s.status === 'VOIDED' && (
            <Callout tone="danger">
              <strong>Venta anulada</strong> {s.voidedAt ? `el ${formatDateTime(s.voidedAt)}` : ''}
              {s.voidedByName ? ` por ${s.voidedByName}` : ''}. Motivo: {s.voidReason}
            </Callout>
          )}
          {pendingRefunds.map((r) => (
            <Callout key={r.id} tone="warning">
              <strong>Devolución pendiente de {formatMoney(r.amountCents)} en Mercado Pago.</strong> {r.error}
              {r.canRetry && (
                <div style={{ marginTop: 8 }}>
                  <Button size="sm" icon={<RotateCw />} loading={busy} onClick={() => void retryRefund()}>
                    Reintentar devolución
                  </Button>
                </div>
              )}
            </Callout>
          ))}
          {doneRefunds.length > 0 && (
            <Callout tone="success">
              Mercado Pago le devolvió {formatMoney(doneRefunds.reduce((a, r) => a + r.amountCents, 0))} al cliente
              {doneRefunds[0]!.refundedAt ? ` el ${formatDateTime(doneRefunds[0]!.refundedAt)}` : ''}.
            </Callout>
          )}
          <dl className="bf-kv">
            <div>
              <dt>Fecha</dt>
              <dd>{formatDateTime(s.createdAt)}</dd>
            </div>
            <div>
              <dt>Sucursal</dt>
              <dd>{s.branchName}</dd>
            </div>
            <div>
              <dt>Atendió</dt>
              <dd>{s.cashierName}</dd>
            </div>
            <div>
              <dt>Total</dt>
              <dd className="bf-num" style={{ fontSize: '1.2rem' }}>
                {formatMoney(s.totalCents)}
              </dd>
            </div>
            {s.costCents !== null && (
              <div>
                <dt>Ganancia bruta</dt>
                <dd className="bf-num bf-text-success">{formatMoney(s.totalCents - s.costCents)}</dd>
              </div>
            )}
          </dl>
          <div className="bf-scroll-box">
            <table className="bf-table">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Cantidad</th>
                  <th>Precio</th>
                  <th style={{ textAlign: 'right' }}>Importe</th>
                </tr>
              </thead>
              <tbody>
                {s.lines.map((l) => (
                  <tr key={l.id}>
                    <td className="bf-cell-main">
                      {l.productName} {l.barcode && <Badge>etiqueta</Badge>}
                    </td>
                    <td className="bf-num">{formatQty(l.unitType, l.qty)}</td>
                    <td className="bf-num">
                      {formatMoney(l.unitPriceCents)}
                      <span className="bf-cell-sub">{priceUnit(l.unitType)}</span>
                    </td>
                    <td className="bf-num" style={{ textAlign: 'right' }}>
                      {formatMoney(l.lineTotalCents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bf-pos-row">
            <span>Pagos</span>
            <span>
              {s.payments.map((p) => `${PAYMENT_METHOD_LABELS[p.method]} ${formatMoney(p.amountCents)}`).join(' · ')}
              {s.discountCents > 0 && ` · Descuento ${formatMoney(s.discountCents)}`}
              {s.changeCents > 0 && ` · Vuelto ${formatMoney(s.changeCents)}`}
            </span>
          </div>

          <FiscalDocsPanel sale={s} />

          {voiding ? (
            <div className="bf-form">
              <Callout tone="warning">
                Al anular, el stock vuelve a los mismos lotes y la venta queda registrada como anulada.
                {s.fiscal.some((d) => d.kind === 'INVOICE' && d.status !== 'REJECTED') &&
                  ' Como tiene factura, sale sola la nota de crédito por el total.'}
              </Callout>
              <Field label="Motivo de la anulación" error={error ?? undefined}>
                <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus placeholder="Ej.: se cobró dos veces" />
              </Field>
              <div className="bf-form-actions">
                <Button onClick={() => setVoiding(false)} disabled={busy}>
                  Volver
                </Button>
                <Button variant="danger" icon={<Ban />} loading={busy} onClick={() => void doVoid()}>
                  Anular venta
                </Button>
              </div>
            </div>
          ) : (
            <div className="bf-form-actions">
              {s.canVoid && (
                <Button variant="ghost" icon={<Ban />} onClick={() => setVoiding(true)} style={{ marginRight: 'auto' }}>
                  Anular
                </Button>
              )}
              {!s.canVoid && s.status === 'COMPLETED' && (
                <span className="bf-cell-sub" style={{ marginRight: 'auto' }}>
                  {s.fiscal.some((d) => d.kind === 'INVOICE' && d.status === 'AUTHORIZED')
                    ? 'La caja ya cerró: para corregirla hacé una nota de crédito.'
                    : 'Solo se anula mientras su caja esté abierta.'}
                </span>
              )}
              <Button icon={<Printer />} onClick={() => printSale(s)}>
                Reimprimir
              </Button>
              <Button variant="primary" onClick={onClose}>
                Listo
              </Button>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}
